'use client';

import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import styles from '../styles/CandidatePhoto.module.css';

const ROLL_THRESHOLD = 40;
const PITCH_THRESHOLD = 20;
const YAW_THRESHOLD = 20;
const DEVIATION_THRESHOLD = 15;
const TOP_MARGIN = 50;
const X_RADIUS_PERCENTAGE = 0.35;
const Y_RADIUS_PERCENTAGE = 0.35;
const TILT_FRAME_COUNT = 3;
const segments = 4;
const gap = 15;
const angleStep = (2 * Math.PI) / segments;

const INSTRUCTION_TITLE = [
  'Position Your Face',
  'Find a well-lit area',
  'Clear view',
  'Rotate your head in circular motion',
];
const INSTRUCTION_DESCRIPTION = [
  'Just make sure your face is in the frame! Keep it all in there.',
  'Choose a well-lit spot with minimal shadows.',
  'Take off any accessories (like glasses, mask etc) that block your face.',
  'Rotate your head slowly in a circular motion, in order to capture all angles.',
];

interface Stage2CameraProps {
  onComplete?: (images: string[]) => void | Promise<void>;
  onBack?: () => void;
  onCaptureComplete?: (images: string[]) => void;
  isLoading?: boolean;
}

export default function Stage2Camera({ onComplete, onBack, onCaptureComplete, isLoading = false }: Stage2CameraProps) {
  const [currentInstructionPage, setCurrentInstructionPage] = useState(0);
  const [showGetStarted, setShowGetStarted] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [captureComplete, setCaptureComplete] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset isSubmitting when parent's loading completes
  useEffect(() => {
    if (!isLoading && isSubmitting) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setIsSubmitting(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isSubmitting]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const overlayBarRef = useRef<HTMLCanvasElement>(null);
  const overlayBarIndicatorRef = useRef<HTMLCanvasElement>(null);
  const videoInstructionsContainerRef = useRef<HTMLDivElement>(null);
  const videoInstructionImageRef = useRef<HTMLImageElement>(null);
  const videoInstructionIconRef = useRef<HTMLDivElement>(null);
  const videoInstructionTextRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraRef = useRef<any>(null);
  const faceMeshRef = useRef<any>(null);
  const centerXRef = useRef<number>(0);
  const centerYRef = useRef<number>(0);
  const radiusXRef = useRef<number>(0);
  const radiusYRef = useRef<number>(0);
  const videoFrameRateRef = useRef<number>(30);
  const segmentFrameCountRef = useRef<number[]>(Array(segments + 1).fill(0));
  const filledSegmentsRef = useRef<Set<number>>(new Set());
  const savedSegmentsRef = useRef<Set<number>>(new Set());
  const positionFaceCompleteRef = useRef<boolean>(false);
  const capturedFramesRef = useRef<string[]>([]);
  const positionFaceImgRef = useRef<HTMLImageElement | null>(null);
  const moveFaceImgRef = useRef<HTMLImageElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Check for scripts and initialize FaceMesh
  useEffect(() => {
    const checkScripts = () => {
      if (
        (window as any).FaceMesh &&
        (window as any).Camera &&
        (window as any).drawConnectors &&
        !faceMeshRef.current
      ) {
        console.log('Initializing FaceMesh...');
        const faceMesh = new (window as any).FaceMesh({
          locateFile: (file: string) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
          },
        });

        faceMesh.setOptions({
          maxNumFaces: 1,
          refineLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });

        faceMesh.onResults(onResults);
        faceMeshRef.current = faceMesh;

        // Preload images
        const moveImg = new Image();
        moveImg.src = '/images/move-face-black.png';
        moveFaceImgRef.current = moveImg;

        const positionImg = new Image();
        positionImg.src = '/images/position-face-black.png';
        positionFaceImgRef.current = positionImg;

        console.log('FaceMesh initialized successfully');
        setScriptsLoaded(true);
      }
    };

    // Check immediately
    checkScripts();

    // Also check periodically in case scripts load after component mounts
    const interval = setInterval(() => {
      if (!faceMeshRef.current) {
        checkScripts();
      } else {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const startCamera = async () => {
    try {
      console.log('Starting camera...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });

      const video = videoRef.current;
      if (!video) {
        console.error('Video element not found');
        setCameraLoading(false);
        return;
      }

      console.log('Stream obtained, setting srcObject...');
      video.srcObject = stream;
      streamRef.current = stream;

      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      videoFrameRateRef.current = settings.frameRate || 30;

      video.onloadedmetadata = () => {
        console.log('Video metadata loaded, dimensions:', video.videoWidth, video.videoHeight);
        centerXRef.current = video.videoWidth / 2;
        centerYRef.current = video.videoHeight / 2;
        radiusXRef.current =
          Math.min(video.videoWidth, video.videoHeight) * X_RADIUS_PERCENTAGE - gap;
        radiusYRef.current =
          Math.min(video.videoWidth, video.videoHeight) * Y_RADIUS_PERCENTAGE - gap;

        // Set canvas dimensions
        if (overlayRef.current) {
          overlayRef.current.width = video.videoWidth;
          overlayRef.current.height = video.videoHeight;
        }
        if (overlayBarRef.current) {
          overlayBarRef.current.width = video.videoWidth;
          overlayBarRef.current.height = video.videoHeight;
        }
        if (overlayBarIndicatorRef.current) {
          overlayBarIndicatorRef.current.width = video.videoWidth;
          overlayBarIndicatorRef.current.height = video.videoHeight;
        }

        // Apply transforms exactly like original selfie.js
        const translateY = 0 //-(video.videoHeight / 2 - radiusYRef.current - gap - TOP_MARGIN);
        const isMobile = window.innerWidth <= 768;
        
        if (video) {
          video.style.transform = `scaleX(-1) translateY(${translateY}px)`;
          video.style.width = `${video.videoWidth}px`;
          video.style.height = `${video.videoHeight}px`;
          video.style.maxWidth = '100%';
          video.style.maxHeight = '100%';
          video.style.objectFit = 'contain' //isMobile ? 'cover' : 'contain';
          if (isMobile) {
            video.style.scale = "1.5";
          }
        }
        if (overlayRef.current) {
          overlayRef.current.style.width = `${video.videoWidth}px`;
          overlayRef.current.style.height = `${video.videoHeight}px`;
          overlayRef.current.style.maxWidth = '100%';
          overlayRef.current.style.maxHeight = '100%';
          // Scale down facemesh on mobile - use CSS transform
          if (isMobile) {
            overlayRef.current.style.scale = "1.5";
            overlayRef.current.style.transform = `translateY(${translateY}px)`;
            overlayRef.current.style.transformOrigin = 'center center';
          } else {
            overlayRef.current.style.transform = `translateY(${translateY}px)`;
          }
        }
        if (overlayBarRef.current) {
          overlayBarRef.current.style.width = `${video.videoWidth}px`;
          overlayBarRef.current.style.height = `${video.videoHeight}px`;
          overlayBarRef.current.style.maxWidth = '100%';
          overlayBarRef.current.style.maxHeight = '100%';
          if (isMobile) {
            overlayBarRef.current.style.scale = "1.5";
            overlayBarRef.current.style.transform = `translateY(${translateY}px)`;
            overlayBarRef.current.style.transformOrigin = 'center center';
          } else {
            overlayBarRef.current.style.transform = `translateY(${translateY}px)`;
          }
        }
        if (overlayBarIndicatorRef.current) {
          overlayBarIndicatorRef.current.style.width = `${video.videoWidth}px`;
          overlayBarIndicatorRef.current.style.height = `${video.videoHeight}px`;
          overlayBarIndicatorRef.current.style.maxWidth = '100%';
          overlayBarIndicatorRef.current.style.maxHeight = '100%';
          if (isMobile) {
            overlayBarIndicatorRef.current.style.scale = "1.5";
            overlayBarIndicatorRef.current.style.transform = `translateY(${translateY}px)`;
            overlayBarIndicatorRef.current.style.transformOrigin = 'center center';
          } else {
            overlayBarIndicatorRef.current.style.transform = `translateY(${translateY}px)`;
          }
        }

        // Position video instructions container
        if (videoInstructionsContainerRef.current) {
          // On mobile, position at bottom; on desktop, use calculated position
          if (window.innerWidth <= 768) {
            videoInstructionsContainerRef.current.style.top = 'auto';
            videoInstructionsContainerRef.current.style.bottom = '2rem';
          } else {
            const topPosition = (radiusYRef.current + gap + TOP_MARGIN) * 2;
            videoInstructionsContainerRef.current.style.top = `${topPosition}px`;
            videoInstructionsContainerRef.current.style.bottom = 'auto';
          }
        }

        // Start MediaPipe camera - wait a bit for scripts if needed
        const initCamera = () => {
          if (faceMeshRef.current && (window as any).Camera) {
            console.log('Initializing MediaPipe Camera...');
            const camera = new (window as any).Camera(video, {
              onFrame: async () => {
                await faceMeshRef.current.send({ image: video });
                setShowVideo(true);
                setCameraLoading(false);
              },
            });

            camera.start();
            cameraRef.current = camera;
            console.log('Camera started');
          } else {
            console.log('Waiting for MediaPipe scripts to load...');
            // Retry after a short delay
            setTimeout(() => {
              if (faceMeshRef.current && (window as any).Camera) {
                initCamera();
              } else {
                console.error('FaceMesh or Camera not available after retry');
                setCameraLoading(false);
              }
            }, 1000);
          }
        };

        initCamera();
      };

      video.onerror = (error) => {
        console.error('Video error:', error);
        setCameraLoading(false);
      };
    } catch (error) {
      console.error('Error accessing front camera:', error);
      alert('Unable to access front camera. Please check permissions.');
      setCameraLoading(false);
    }
  };

  const getFaceAngles = (landmarks: any[]) => {
    const LEFT_EYE_INDEX = 33;
    const RIGHT_EYE_INDEX = 263;
    const NOSE_TIP_INDEX = 1;
    const CHIN_INDEX = 152;

    const leftEye = landmarks[LEFT_EYE_INDEX];
    const rightEye = landmarks[RIGHT_EYE_INDEX];
    const nose = landmarks[NOSE_TIP_INDEX];
    const chin = landmarks[CHIN_INDEX];

    const dx = rightEye.x - leftEye.x;
    const dy = rightEye.y - leftEye.y;
    const rollRad = Math.atan2(dy, dx);
    const roll = (rollRad * 180) / Math.PI;

    const eyeMidX = (leftEye.x + rightEye.x) / 2;
    const eyeMidZ = (leftEye.z + rightEye.z) / 2;
    const yawRad = Math.atan2(nose.x - eyeMidX, -(nose.z - eyeMidZ));
    const yaw = (yawRad * 180) / Math.PI;

    const pitchRad = Math.atan2(chin.y - nose.y, -(chin.z - nose.z));
    const calculatedPitch = (pitchRad * 180) / Math.PI;
    const unNormalizedPitch = calculatedPitch - 110;
    const pitch = ((unNormalizedPitch + 180) % 360) - 180;

    return { roll, pitch, yaw };
  };

  const isFrameDark = (threshold = 70): boolean => {
    const video = videoRef.current;
    if (!video) return false;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let totalLuminance = 0;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
      totalLuminance += luminance;
    }

    const avgLuminance = totalLuminance / (data.length / 4);
    return avgLuminance < threshold;
  };

  const saveFrame = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataURL = canvas.toDataURL('image/jpeg');
      capturedFramesRef.current.push(dataURL);
    }
  };

  const headPoseToAngle = (roll: number, pitch: number, yaw: number): number => {
    const pitchRad = (pitch * Math.PI) / 180;
    const yawRad = (yaw * Math.PI) / 180;
    const x = Math.sin(yawRad);
    const y = -Math.sin(pitchRad);
    let angle = (Math.atan2(y, x) * 180) / Math.PI;
    angle = (angle + 360) % 360;
    return angle;
  };

  const drawLineAtAngle = (angleDeg: number, color: string, ctxBarIndicator: CanvasRenderingContext2D) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    const x1 = centerXRef.current + radiusXRef.current * Math.cos(angleRad);
    const y1 = centerYRef.current + radiusYRef.current * Math.sin(angleRad);
    const x2 = centerXRef.current + (radiusXRef.current + gap) * Math.cos(angleRad);
    const y2 = centerYRef.current + (radiusYRef.current + gap) * Math.sin(angleRad);

    ctxBarIndicator.beginPath();
    ctxBarIndicator.moveTo(x1, y1);
    ctxBarIndicator.lineTo(x2, y2);
    ctxBarIndicator.strokeStyle = color;
    ctxBarIndicator.lineWidth = 5;
    ctxBarIndicator.stroke();
  };

  const animateFillSegment = (
    segmentIndex: number,
    endAngle: number,
    ctxBar: CanvasRenderingContext2D,
  ) => {
    const startAngle = segmentIndex * angleStep;
    const endAngleLimit = startAngle + angleStep;

    if (endAngle >= endAngleLimit) {
      if (endAngle === endAngleLimit) {
        filledSegmentsRef.current.add(segmentIndex);
        if (filledSegmentsRef.current.size > segments) {
          // All segments filled - capture complete
          setCaptureComplete(true);
          // Hide video and canvas elements
          if (videoRef.current) {
            videoRef.current.style.display = 'none';
          }
          if (overlayRef.current) {
            overlayRef.current.style.display = 'none';
          }
          if (overlayBarRef.current) {
            overlayBarRef.current.style.display = 'none';
          }
          if (overlayBarIndicatorRef.current) {
            overlayBarIndicatorRef.current.style.display = 'none';
          }
          return;
        }
        return;
      }
      endAngle = endAngleLimit;
    } else {
      endAngle += 0.03;
    }

    // Draw filled segment using even-odd fill rule
    ctxBar.beginPath();
    ctxBar.ellipse(
      centerXRef.current,
      centerYRef.current,
      radiusXRef.current + gap,
      radiusYRef.current + gap,
      0,
      startAngle,
      endAngle,
      false,
    );

    const endX = centerXRef.current + radiusXRef.current * Math.cos(endAngle);
    const endY = centerYRef.current + radiusYRef.current * Math.sin(endAngle);
    ctxBar.lineTo(endX, endY);

    ctxBar.ellipse(
      centerXRef.current,
      centerYRef.current,
      radiusXRef.current,
      radiusYRef.current,
      0,
      endAngle,
      startAngle,
      true,
    );

    ctxBar.closePath();
    ctxBar.fillStyle = '#009e4a';
    ctxBar.fill('evenodd');

    animationFrameRef.current = requestAnimationFrame(() =>
      animateFillSegment(segmentIndex, endAngle, ctxBar),
    );
  };

  const postProcess = (
    roll: number,
    pitch: number,
    yaw: number,
    ctxBar: CanvasRenderingContext2D | null,
    ctxBarIndicator: CanvasRenderingContext2D | null,
  ) => {
    if (!ctxBar || !ctxBarIndicator) return;

    const angle = headPoseToAngle(roll, pitch, yaw);
    const normalized_angle = (angle - 180) % 360;
    const deviation = Math.sqrt(pitch ** 2 + yaw ** 2);

    if (deviation > DEVIATION_THRESHOLD) {
      const angleRad = ((normalized_angle % 360) * Math.PI) / 180;
      const currentSegmentIndex = Math.floor(angleRad / angleStep);
      let currentSegmentMap = currentSegmentIndex;
      if (currentSegmentMap < 0) {
        currentSegmentMap += segments;
      }

      const segmentFrameCount = segmentFrameCountRef.current;
      segmentFrameCount[currentSegmentMap]++;
      segmentFrameCount.forEach((_, segmentIndex) => {
        if (segmentIndex !== currentSegmentMap) {
          segmentFrameCount[segmentIndex] = 0;
        }
      });

      if (segmentFrameCount[currentSegmentMap] > TILT_FRAME_COUNT) {
        if (!savedSegmentsRef.current.has(currentSegmentMap)) {
          savedSegmentsRef.current.add(currentSegmentMap);
          saveFrame();
          const startAngle = currentSegmentMap * angleStep;
          const endAngle = startAngle;
          requestAnimationFrame(() => animateFillSegment(currentSegmentMap, endAngle, ctxBar));
        }
      }
      drawLineAtAngle(normalized_angle, '#b80000', ctxBarIndicator);
    }
  };

  const onResults = (results: any) => {
    const canvas = overlayRef.current;
    const overlayBar = overlayBarRef.current;
    const overlayBarIndicator = overlayBarIndicatorRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const ctxBar = overlayBar?.getContext('2d');
    const ctxBarIndicator = overlayBarIndicator?.getContext('2d');
    if (!ctx) return;

    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctxBarIndicator?.clearRect(0, 0, overlayBarIndicator?.width || 0, overlayBarIndicator?.height || 0);

    let faceInsideOval = false;
    let faceStraight = false;
    let roll = 0;
    let pitch = 0;
    let yaw = 0;

    if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
      const origLandmarks = results.multiFaceLandmarks[0];
      const landmarks = origLandmarks.map((p: any) => ({
        ...p,
        x: 1 - p.x,
      }));

      ({ roll, pitch, yaw } = getFaceAngles(origLandmarks));

      const xs = landmarks.map((p: any) => p.x * canvas.width);
      const ys = landmarks.map((p: any) => p.y * canvas.height);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const faceWidth = maxX - minX;
      const faceHeight = maxY - minY;

      const FACEMESH_FACE_OVAL = (window as any).FACEMESH_FACE_OVAL || [];
      const faceWithinOval = FACEMESH_FACE_OVAL.every(([startIdx]: [number, number]) => {
        const point = landmarks[startIdx];
        const x = point.x * canvas.width;
        const y = point.y * canvas.height;
        const normX = (x - centerXRef.current) / radiusXRef.current;
        const normY = (y - centerYRef.current) / radiusYRef.current;
        return normX * normX + normY * normY <= 1;
      });

      const meetsSizeRequirement =
        faceWidth >= radiusXRef.current * 0.5 && faceHeight >= radiusYRef.current * 0.5;
      faceStraight = Math.abs(roll) < ROLL_THRESHOLD;
      faceInsideOval = faceWithinOval && meetsSizeRequirement;

      // Draw face mesh wireframe (exactly like original)
      if ((window as any).drawConnectors && (window as any).FACEMESH_TESSELATION) {
        (window as any).drawConnectors(ctx, landmarks, (window as any).FACEMESH_TESSELATION, {
          color: '#C0C0C070',
          lineWidth: 1,
        });
      }
      
      // Don't draw any bounding boxes or rectangles - just the wireframe mesh
    }

    // Display roll/pitch/yaw for debugging (like original) - commented out for production
    // Uncomment below for debugging
    // const deviation = Math.sqrt(pitch ** 2 + yaw ** 2);
    // ctx.font = '18px Arial';
    // ctx.fillStyle = 'red';
    // ctx.fillText(`Roll: ${roll.toFixed(1)}`, canvas.width / 2 - 60, 120);
    // ctx.fillText(`Pitch: ${pitch.toFixed(1)}`, canvas.width / 2 - 60, 140);
    // ctx.fillText(`Yaw: ${yaw.toFixed(1)}`, canvas.width / 2 - 60, 160);
    // ctx.fillText(`Deviation: ${deviation.toFixed(1)}`, canvas.width / 2 - 60, 180);

    const threshold = Math.min(videoFrameRateRef.current, 30);
    const segmentFrameCount = segmentFrameCountRef.current;

    if (segmentFrameCount[segments] <= threshold) {
      if (isFrameDark()) {
        if (videoInstructionIconRef.current) {
          videoInstructionIconRef.current.style.display = 'none';
        }
        if (videoInstructionImageRef.current && positionFaceImgRef.current) {
          videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
        }
        if (videoInstructionTextRef.current) {
          videoInstructionTextRef.current.innerText = 'Environment is too dark';
        }
        segmentFrameCount[segments] = 0;
      } else if (faceInsideOval) {
        if (
          !(
            Math.abs(roll) < ROLL_THRESHOLD &&
            Math.abs(pitch) < PITCH_THRESHOLD &&
            Math.abs(yaw) < YAW_THRESHOLD
          )
        ) {
          if (videoInstructionIconRef.current) {
            videoInstructionIconRef.current.style.display = 'none';
          }
          if (videoInstructionImageRef.current && moveFaceImgRef.current) {
            videoInstructionImageRef.current.src = moveFaceImgRef.current.src;
          }
          if (videoInstructionTextRef.current) {
            videoInstructionTextRef.current.innerText = 'Position Your Face';
          }
          segmentFrameCount[segments] = 0;
        } else {
          segmentFrameCount[segments] += 1;
        }
      } else {
        segmentFrameCount[segments] = 0;
        if (videoInstructionIconRef.current) {
          videoInstructionIconRef.current.style.display = 'none';
        }
        if (videoInstructionImageRef.current && positionFaceImgRef.current) {
          videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
        }
        if (videoInstructionTextRef.current) {
          videoInstructionTextRef.current.innerText = 'Position Your Face';
        }
      }
    }

    if (segmentFrameCount[segments] > threshold) {
      if (!filledSegmentsRef.current.has(segments)) {
        saveFrame();
        filledSegmentsRef.current.add(segments);
        if (videoInstructionImageRef.current && videoInstructionIconRef.current) {
          videoInstructionImageRef.current.style.display = 'none';
          videoInstructionIconRef.current.style.display = 'block';
          const positionFaceAnimation = () => {
            setTimeout(() => {
              positionFaceCompleteRef.current = true;
            }, 100);
            if (videoInstructionIconRef.current) {
              videoInstructionIconRef.current.removeEventListener('animationend', positionFaceAnimation);
              videoInstructionIconRef.current.style.display = 'none';
            }
            if (videoInstructionImageRef.current && moveFaceImgRef.current) {
              videoInstructionImageRef.current.style.display = 'block';
              videoInstructionImageRef.current.src = moveFaceImgRef.current.src;
            }
            if (videoInstructionTextRef.current) {
              videoInstructionTextRef.current.innerText = 'Rotate your head in circle';
            }
          };
          if (videoInstructionIconRef.current) {
            videoInstructionIconRef.current.addEventListener('animationend', positionFaceAnimation);
          }
        }
      }
    }

    const centerX = centerXRef.current;
    const centerY = centerYRef.current;
    const radiusX = radiusXRef.current;
    const radiusY = radiusYRef.current;

    if (positionFaceCompleteRef.current) {
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.ellipse(centerX, centerY, radiusX + gap, radiusY + gap, 0, 0, 2 * Math.PI);
      ctx.fillStyle = '#ffffff';
      ctx.fill('evenodd');
      ctx.restore();

      ctx.beginPath();
      ctx.strokeStyle = '#C0C0C0';
      ctx.lineWidth = 4;
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.ellipse(centerX, centerY, radiusY + gap, radiusX + gap, 0, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.closePath();
      ctx.fillStyle = '#C0C0C0';
      ctx.fill('evenodd');

      if (isFrameDark()) {
        if (videoInstructionIconRef.current) {
          videoInstructionIconRef.current.style.display = 'none';
        }
        if (videoInstructionImageRef.current && positionFaceImgRef.current) {
          videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
        }
        if (videoInstructionTextRef.current) {
          videoInstructionTextRef.current.innerText = 'Environment is too dark';
        }
      } else if (faceInsideOval) {
        if (videoInstructionImageRef.current && moveFaceImgRef.current) {
          videoInstructionImageRef.current.src = moveFaceImgRef.current.src;
        }
        if (videoInstructionIconRef.current) {
          videoInstructionIconRef.current.style.display = 'none';
        }
        if (!faceStraight) {
          if (videoInstructionTextRef.current) {
            videoInstructionTextRef.current.innerText = 'Position Your Face';
          }
        } else {
          if (videoInstructionTextRef.current) {
            videoInstructionTextRef.current.innerText = 'Rotate your head in circle';
          }
          if (ctxBar && ctxBarIndicator) {
            postProcess(roll, pitch, yaw, ctxBar, ctxBarIndicator);
          }
        }
      } else {
        if (videoInstructionTextRef.current) {
          videoInstructionTextRef.current.innerText = 'Position Your Face';
        }
        if (videoInstructionImageRef.current && positionFaceImgRef.current) {
          videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
        }
      }
    } else {
      const cornerRadius = 10;
      const edgeGap = 60;
      const g = edgeGap;
      const r = cornerRadius;
      const halfWidth = 0.75 * radiusX;
      const left = centerX - halfWidth;
      const top = centerY - halfWidth;
      const right = centerX + halfWidth;
      const bottom = centerY + halfWidth;

      ctx.beginPath();
      ctx.moveTo(left + r, top);
      ctx.arcTo(left, top, left, top + r, r);
      ctx.moveTo(left, top + r);
      ctx.lineTo(left, centerY - g);
      ctx.moveTo(left, centerY + g);
      ctx.lineTo(left, bottom - r);
      ctx.arcTo(left, bottom, left + r, bottom, r);
      ctx.lineTo(centerX - g, bottom);
      ctx.moveTo(centerX + g, bottom);
      ctx.lineTo(right - r, bottom);
      ctx.arcTo(right, bottom, right, bottom - r, r);
      ctx.lineTo(right, centerY + g);
      ctx.moveTo(right, centerY - g);
      ctx.lineTo(right, top + r);
      ctx.arcTo(right, top, right - r, top, r);
      ctx.lineTo(centerX + g, top);
      ctx.moveTo(centerX - g, top);
      ctx.lineTo(left + r, top);

      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 4;
      ctx.stroke();

      // Draw white mask outside the corner brackets
      const displacement = 25;
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.rect(
        left - displacement,
        top - displacement,
        right - left + displacement * 2,
        bottom - top + displacement * 2,
      );

      ctx.fillStyle = '#ffffff';
      ctx.fill('evenodd');
      ctx.restore();
    }
  };

  const viewInstruction = () => {
    setShowGetStarted(false);
    setShowInstructions(true);
  };

  const nextInstruction = () => {
    if (currentInstructionPage >= INSTRUCTION_TITLE.length - 1) {
      setShowInstructions(false);
      setShowGetStarted(false);
      setCameraLoading(true);
      startCamera();
      return;
    }
    if (currentInstructionPage < INSTRUCTION_TITLE.length - 1) {
      setCurrentInstructionPage((prev) => prev + 1);
    }
  };

  const prevInstruction = () => {
    if (currentInstructionPage > 0) {
      setCurrentInstructionPage((prev) => prev - 1);
    }
  };

  const skipInstruction = () => {
    setShowInstructions(false);
    setShowGetStarted(false);
    setCameraLoading(true);
    startCamera();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div className={styles.mainWrapper}>
      <Script
        src="https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js"
        strategy="afterInteractive"
        onLoad={() => {
          console.log('Camera utils loaded');
          setScriptsLoaded(true);
        }}
      />
      <Script
        src="https://cdn.jsdelivr.net/npm/@mediapipe/control_utils/control_utils.js"
        strategy="afterInteractive"
        onLoad={() => console.log('Control utils loaded')}
      />
      <Script
        src="https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js"
        strategy="afterInteractive"
        onLoad={() => console.log('Drawing utils loaded')}
      />
      <Script
        src="https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js"
        strategy="afterInteractive"
        onLoad={() => {
          console.log('Face mesh loaded');
          setScriptsLoaded(true);
        }}
      />

      {cameraLoading && (
        <div className={styles.cameraLoading} id="camera-loading">
          Starting Camera
          <br />
          Please wait...
        </div>
      )}

      <div className={styles.contentWrapper}>
        <div
          className={styles.homeContainer}
          id="home-container"
          style={{ display: showVideo ? 'none' : 'flex' }}
        >
          {showGetStarted && (
            <div className={styles.getStartedPage}>
              <div className={styles.headingText}>&ldquo;Let&apos;s Get You Ready&rdquo;</div>
              <div className={styles.headingDescription}>
                We&apos;ll guide you through a few simple steps to prepare you for the face scan.
              </div>
              <button type="button" className={styles.button} onClick={viewInstruction}>
                View Instructions
              </button>
            </div>
          )}

          {showInstructions && (
            <div className={styles.instructionPage}>
              <div className={styles.instructionContent}>
                <div className={styles.instructionMainContent}>
                  <div className={styles.instructionTextSection}>
                    <button
                      type="button"
                      className={`${styles.navBtn} ${styles.skipBtn}`}
                      onClick={skipInstruction}
                    >
                      Skip
                      <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M1.5 1L8.5 8L1.5 15"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.instructionHeadingContainer}>
                      <div className={styles.instructionHeading}>Instruction</div>
                      <div className={styles.instructionPageNo}>
                        <span className={styles.currentPage}>{currentInstructionPage + 1}</span> of{' '}
                        {INSTRUCTION_TITLE.length}
                      </div>
                      <div className={styles.instructionTitle}>
                        {INSTRUCTION_TITLE[currentInstructionPage]}
                      </div>
                    </div>
                    <div className={styles.instructionDescription}>
                      {INSTRUCTION_DESCRIPTION[currentInstructionPage]}
                    </div>
                    <div className={styles.pageIndicatorContainer}>
                      {INSTRUCTION_TITLE.map((_, idx) => (
                        <div
                          key={idx}
                          id={`page-indicator-${idx + 1}`}
                          className={`${styles.pageIndicator} ${
                            idx === currentInstructionPage ? styles.pageIndicatorActive : ''
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className={styles.instructionImageContainer}>
                    <img
                      id="instruction-image"
                      className={styles.instructionImage}
                      src={`/images/instruction-${currentInstructionPage + 1}.png`}
                      alt="Instruction Image"
                    />
                  </div>
                  <div className={styles.instructionButtonContainer}>
                    <button
                      id="prev-button"
                      type="button"
                      className={`${styles.button} ${styles.instructionButton} ${
                        currentInstructionPage === 0 ? styles.hidden : ''
                      }`}
                      onClick={prevInstruction}
                    >
                      <svg width="7" height="14" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8.5 15L1.5 8L8.5 1"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Back
                    </button>
                    <button
                      id="next-button"
                      type="button"
                      className={`${styles.button} ${styles.instructionButton} ${styles.nextButton}`}
                      onClick={nextInstruction}
                    >
                      <div id="next-button-text">
                        {currentInstructionPage === INSTRUCTION_TITLE.length - 1
                          ? 'Start'
                          : 'Next'}
                      </div>
                      <svg width="7" height="14" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M1.5 1L8.5 8L1.5 15"
                          stroke="#ffffff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className={styles.videoContainer}
          id="video-container"
          style={{ display: showVideo ? 'flex' : 'none' }}
        > 
        <div className={styles.videoContainerInner}>
          <video id="video" ref={videoRef} style={{ scale: 1.75 }} autoPlay playsInline />
          <canvas id="overlay" ref={overlayRef} className={styles.overlay} style={{ scale: 1.75 }} />
          <canvas id="overlay-bar" ref={overlayBarRef} className={`${styles.overlay} ${styles.overlayBar}`} style={{ scale: 1.75 }} />
          <canvas
            id="overlay-bar-indicator"
            ref={overlayBarIndicatorRef}
            className={`${styles.overlay} ${styles.overlayBarIndicator}`}
            style={{ scale: 1.75 }}
          />
        </div>
          <div id="video-instructions-container" ref={videoInstructionsContainerRef} className={styles.videoInstructionsContainer}>
            <div className={styles.videoInstructionImageWrapper}>
              <img
                id="video-instruction-image"
                ref={videoInstructionImageRef}
                className={styles.videoInstructionImageIcon}
                src="/images/position-face-black.png"
                alt="Instruction Image"
              />
              <div id="video-instruction-icon" ref={videoInstructionIconRef} className={styles.videoInstructionIcon}>
                <div className={styles.videoInstructionIconBg}></div>
                <div className={styles.videoInstructionCheckmark}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.75 12L10.58 14.83L16.25 9.17004"
                      stroke="#ffffff"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div id="video-instruction-text" ref={videoInstructionTextRef} className={styles.videoInstructionText}>
              Position Your Face
            </div>
          </div>
        </div>
      </div>

      {captureComplete && (
        <div className={styles.completedContainer}>
          <div className={styles.completedInnerContainer}>
            <div className={styles.completedIcon}>
              <div className={styles.completedIconBg}></div>
              <div className={styles.completedCheckmark}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.75 12L10.58 14.83L16.25 9.17004"
                    stroke="#ffffff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div>Capture Complete!</div>
            <button
              type="button"
              className={styles.button}
              disabled={isSubmitting || isLoading}
              onClick={async () => {
                if (isSubmitting || isLoading) {
                  return;
                }
                const images = capturedFramesRef.current;
                setIsSubmitting(true);
                try {
                  if (onCaptureComplete) {
                    onCaptureComplete(images);
                  }
                  if (onComplete) {
                    // Wait for the async operation to complete
                    const result = onComplete(images);
                    if (result instanceof Promise) {
                      await result;
                    }
                  }
                } catch (error) {
                  console.error('Error in onComplete:', error);
                  setIsSubmitting(false);
                }
                // Note: isSubmitting will be managed by isLoading prop from parent
                // If isLoading becomes false and we're still here, reset isSubmitting
              }}
            >
              {(isSubmitting || isLoading) ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

